/**
 * Mycelium Form Submission Connector
 *
 * Listens for `forms.form_submission_received` events and translates them
 * into downstream domain events according to the form's configured action
 * mappings (`form_definitions.settings.actions[]`).
 *
 * Supported action types (declared by `@flowstate/forms` `FormActionType`):
 *   • create_client       → publishes `crm.client_create_requested`
 *   • create_task         → publishes `task.task_create_requested`
 *   • create_opportunity  → publishes `sales.opportunity_create_requested`
 *   • create_invoice_line → publishes `accounting.invoice_line_create_requested`
 *   • trigger_webhook      → enqueues `webhook.webhook_delivery_requested`
 *   • trigger_n8n          → deprecated alias for trigger_webhook
 *   • send_email          → publishes `notification.email_send_requested`
 *   • post_slack          → publishes `notification.slack_post_requested`
 *   • publish_event       → publishes a free-form event using config.domain/type
 *
 * After dispatch, publishes `forms.form_submission_completed` with the
 * list of action ids that ran. All handler logic is wrapped in try/catch
 * — handler errors must never crash the backbone.
 *
 * @module mycelium-form-submission
 * @since 2026-05-06
 */

import { FormSubmissionReceivedPayloadSchema } from "@flowstate/mycelium-core";
import {
  myceliumEventBackbone,
  type MyceliumEvent,
} from "../mycelium-event-backbone.service";
import { getMyceliumTransport } from "../mycelium-transport-registry";
import { logger } from "../../lib/logger";
import { typedSupabase } from "../../lib/typed-supabase";
import { enqueueWebhookDelivery } from "../mycelium-webhook-enqueue";

// ── Action types (mirrors @flowstate/forms `FormActionConfig`) ──────────

type FormActionType =
  | "create_client"
  | "create_task"
  | "create_opportunity"
  | "create_invoice_line"
  | "trigger_webhook"
  | "trigger_n8n"
  | "send_email"
  | "post_slack"
  | "publish_event";

interface FormFieldMapping {
  from: string;
  to: string;
  transform?: "lowercase" | "uppercase" | "trim" | "to_number";
  default?: unknown;
}

interface FormActionConfig {
  id: string;
  type: FormActionType;
  label?: string;
  /** When omitted, the action always runs. Gate is evaluated on submission data. */
  when?: { connector: "AND" | "OR"; conditions: unknown[] };
  mappings?: FormFieldMapping[];
  config?: Record<string, unknown>;
}

interface FormSubmissionRow {
  id: string;
  form_id: string;
  organization_id: string | null;
  data: Record<string, unknown>;
}

interface FormDefinitionRow {
  id: string;
  organization_id: string;
  settings: { actions?: FormActionConfig[] } | null;
}

// ── Helpers ─────────────────────────────────────────────────────────────

function applyTransform(
  value: unknown,
  t?: FormFieldMapping["transform"],
): unknown {
  if (value === undefined || value === null) return value;
  switch (t) {
    case "lowercase":
      return String(value).toLowerCase();
    case "uppercase":
      return String(value).toUpperCase();
    case "trim":
      return String(value).trim();
    case "to_number":
      return Number(value);
    default:
      return value;
  }
}

function resolvePayload(
  action: FormActionConfig,
  data: Record<string, unknown>,
): Record<string, unknown> {
  const out: Record<string, unknown> = {};
  for (const m of action.mappings ?? []) {
    const raw = data[m.from];
    const value = raw === undefined || raw === "" ? m.default : raw;
    out[m.to] = applyTransform(value, m.transform);
  }
  return out;
}

/**
 * Map an action type to a `(domain, type)` tuple. `publish_event` reads
 * the target from the action `config`.
 */
function actionTarget(action: FormActionConfig): { domain: string; type: string } {
  switch (action.type) {
    case "create_client":
      return { domain: "crm", type: "client_create_requested" };
    case "create_task":
      return { domain: "task", type: "task_create_requested" };
    case "create_opportunity":
      return { domain: "sales", type: "opportunity_create_requested" };
    case "create_invoice_line":
      return { domain: "accounting", type: "invoice_line_create_requested" };
    case "trigger_webhook":
    case "trigger_n8n":
      return { domain: "webhook", type: "webhook_delivery_requested" };
    case "send_email":
      return { domain: "notification", type: "email_send_requested" };
    case "post_slack":
      return { domain: "notification", type: "slack_post_requested" };
    case "publish_event": {
      const cfg = action.config ?? {};
      return {
        domain: typeof cfg.domain === "string" ? cfg.domain : "system",
        type: typeof cfg.type === "string" ? cfg.type : "form_action_event",
      };
    }
    default:
      return { domain: "system", type: "form_action_event" };
  }
}

// ── Connector ───────────────────────────────────────────────────────────

export class FormSubmissionConnector {
  private initialized = false;

  async initialize(): Promise<void> {
    if (this.initialized) return;
    logger.info("📝 Form Submission connector initializing");

    myceliumEventBackbone.subscribe({
      name: "FormSubmissionConnector",
      domains: ["forms" as import("@flowstate/mycelium-core").EventDomain],
      eventTypes: ["form_submission_received"],
      handler: this.handleSubmission.bind(this),
      priority: 5,
    });

    this.initialized = true;
    logger.info("✅ Form Submission connector ready");
  }

  async dispose(): Promise<void> {
    this.initialized = false;
  }

  private async handleSubmission(event: MyceliumEvent): Promise<void> {
    try {
      const parsed = FormSubmissionReceivedPayloadSchema.safeParse(event.payload ?? {});
      if (!parsed.success) {
        logger.warn("FormSubmissionConnector: invalid payload", {
          eventId: event.id,
          issues: parsed.error.issues.map((i) => `${i.path.join(".")}: ${i.message}`),
        });
        return;
      }
      const { submission_id: submissionId, form_id: formId, status = "complete" } = parsed.data;

      if (!submissionId || !formId) {
        logger.debug("FormSubmissionConnector: missing ids — skipping", {
          eventId: event.id,
        });
        return;
      }
      if (status === "spam") {
        logger.debug("FormSubmissionConnector: skipping spam submission", {
          submissionId,
        });
        return;
      }

      // Fetch the submission + form definition (settings.actions).
      const [{ data: submission }, { data: form }] = await Promise.all([
        typedSupabase
          .from("form_submissions")
          .select("id, form_id, organization_id, data")
          .eq("id", submissionId)
          .maybeSingle(),
        typedSupabase
          .from("form_definitions")
          .select("id, organization_id, settings")
          .eq("id", formId)
          .maybeSingle(),
      ]);

      if (!submission || !form) {
        logger.warn("FormSubmissionConnector: submission or form missing", {
          submissionId,
          formId,
        });
        return;
      }

      const sub = submission as unknown as FormSubmissionRow;
      const def = form as unknown as FormDefinitionRow;
      const actions = def.settings?.actions ?? [];
      if (actions.length === 0) {
        // No mappings configured — just emit the completion event.
        await this.publishCompleted(event, sub, []);
        return;
      }

      const ranActionIds: string[] = [];
      const transport = getMyceliumTransport();

      for (const action of actions) {
        try {
          if (action.type === "trigger_webhook" || action.type === "trigger_n8n") {
            const orgId = sub.organization_id ?? event.orgId;
            if (!orgId) {
              logger.warn("FormSubmissionConnector: trigger_webhook skipped — missing org", {
                submissionId: sub.id,
                actionId: action.id,
              });
              continue;
            }

            await enqueueWebhookDelivery({
              sourceDomain: "forms",
              sourceType: "form_submission_received",
              sourceEventId: String(event.id),
              orgId,
              eventPayload: {
                ...resolvePayload(action, sub.data),
                submission_id: sub.id,
                form_id: sub.form_id,
                source_action_id: action.id,
                source: "FormSubmissionConnector",
              },
              originalTimestamp: new Date(event.timestamp ?? Date.now()).toISOString(),
              source: "FormSubmissionConnector",
              userId: event.userId ?? undefined,
            });

            ranActionIds.push(action.id);
            continue;
          }

          const target = actionTarget(action);
          const payload: Record<string, unknown> = {
            ...resolvePayload(action, sub.data),
            source_submission_id: sub.id,
            source_form_id: sub.form_id,
            source_action_id: action.id,
            source: "FormSubmissionConnector",
          };

          await transport.publish({
            domain: target.domain,
            type: target.type,
            userId: event.userId,
            orgId: sub.organization_id ?? undefined,
            payload,
          } as Parameters<typeof transport.publish>[0]);

          ranActionIds.push(action.id);
        } catch (actionErr) {
          logger.error("FormSubmissionConnector: action dispatch failed", {
            eventType: event.type,
            eventId: event.id,
            actionId: action.id,
            error:
              actionErr instanceof Error ? actionErr.message : String(actionErr),
          });
        }
      }

      await this.publishCompleted(event, sub, ranActionIds);
    } catch (error) {
      logger.error("FormSubmissionConnector handler error", {
        eventType: event.type,
        eventId: event.id,
        error: error instanceof Error ? error.message : String(error),
      });
    }
  }

  private async publishCompleted(
    sourceEvent: MyceliumEvent,
    submission: FormSubmissionRow,
    ranActionIds: string[],
  ): Promise<void> {
    try {
      const transport = getMyceliumTransport();
      await transport.publish({
        domain: "forms",
        type: "form_submission_completed",
        userId: sourceEvent.userId,
        orgId: submission.organization_id ?? undefined,
        payload: {
          submission_id: submission.id,
          form_id: submission.form_id,
          actions_run: ranActionIds,
          source: "FormSubmissionConnector",
        },
      } as Parameters<typeof transport.publish>[0]);
    } catch (publishErr) {
      logger.error("FormSubmissionConnector: completed event publish failed", {
        submissionId: submission.id,
        error:
          publishErr instanceof Error ? publishErr.message : String(publishErr),
      });
    }
  }
}

export const formSubmissionConnector = new FormSubmissionConnector();
